import { createFileRoute, Link } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { AccountHeader } from '@/components/account-nav'
import { Button } from '@/components/ui/button'
import {
  ApiError,
  adminCreateOffer,
  adminDeleteAffiliate,
  adminDeleteOffer,
  adminExpiringComps,
  adminExtendComp,
  adminGetPaypalSettings,
  adminGrantComp,
  adminListAffiliates,
  adminListComps,
  adminListOffers,
  adminListUsers,
  adminReopenUser,
  adminReviewAffiliate,
  adminRevokeComp,
  adminTempPassword,
  adminUpdateOffer,
  adminUpdatePaypalSettings,
  getMe,
  type AdminUserRow,
  type AffiliateApplication,
  type AuthUser,
  type Comp,
  type Offer,
  type OfferInput,
  type PaypalSettings,
} from '@/lib/kwykedit-api'

export const Route = createFileRoute('/admin')({
  component: AdminPage,
})

type Tab = 'offers' | 'comps' | 'expiring' | 'paypal' | 'users' | 'affiliates'

const inputClassName =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none'
const thClassName = 'border-b border-border px-3 py-2 text-left text-xs font-medium text-muted-foreground'
const tdClassName = 'border-b border-border px-3 py-2 text-sm'

function fmtDateTime(sec: number | null): string {
  if (!sec) return '—'
  return new Date(sec * 1000).toLocaleString()
}

function secToDateInput(sec: number | null): string {
  if (!sec) return ''
  const d = new Date(sec * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function dateInputToSec(value: string): number | null {
  if (!value) return null
  const t = new Date(`${value}T12:00:00`).getTime()
  return Number.isNaN(t) ? null : Math.floor(t / 1000)
}

function useAdminGate() {
  const [me, setMe] = useState<AuthUser | null>(null)
  const [checking, setChecking] = useState(true)
  const [denied, setDenied] = useState(false)

  useEffect(() => {
    let live = true
    void getMe()
      .then((user) => {
        if (live) {
          setMe(user)
          setDenied(!user.is_admin)
        }
      })
      .catch(() => {
        if (live) setDenied(true)
      })
      .finally(() => {
        if (live) setChecking(false)
      })
    return () => {
      live = false
    }
  }, [])

  return { me, checking, denied }
}

// --- offers ---------------------------------------------------------------

const emptyOffer: OfferInput = {
  code: '',
  percent_off: 0,
  amount_off_cents: 0,
  first_month_only: true,
  expires_at: null,
  max_uses: null,
  affiliate_id: null,
  active: true,
}

function OfferForm({
  initial,
  submitLabel,
  onDone,
}: {
  initial: OfferInput
  submitLabel: string
  onDone: (offer: Offer) => void
}) {
  const [form, setForm] = useState<OfferInput>(initial)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const set = <K extends keyof OfferInput>(key: K, value: OfferInput[K]) => {
    setForm((f) => ({ ...f, [key]: value }))
  }
  const isEdit = 'id' in initial && typeof (initial as { id?: number }).id === 'number'

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setPending(true)
    setError(null)
    const payload: OfferInput = {
      ...form,
      code: form.code.trim().toUpperCase(),
      percent_off: Number(form.percent_off) || 0,
      amount_off_cents: Number(form.amount_off_cents) || 0,
      max_uses: form.max_uses ? Number(form.max_uses) : null,
      affiliate_id: form.affiliate_id?.trim() ? form.affiliate_id.trim() : null,
    }
    const task =
      isEdit
        ? adminUpdateOffer((initial as { id: number }).id, payload)
        : adminCreateOffer(payload)
    void task
      .then(onDone)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Save failed.')
      })
      .finally(() => setPending(false))
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <label className="text-xs text-muted-foreground">
        Code{isEdit ? ' (immutable)' : ''}
        <input
          value={form.code}
          onChange={(e) => set('code', e.target.value.toUpperCase())}
          required
          disabled={isEdit}
          pattern="[A-Za-z0-9_-]{3,32}"
          placeholder="FRIEND20"
          className={inputClassName}
        />
      </label>
      <label className="text-xs text-muted-foreground">
        % off
        <input
          type="number"
          min={0}
          max={100}
          value={form.percent_off ?? 0}
          onChange={(e) => set('percent_off', Number(e.target.value))}
          className={inputClassName}
        />
      </label>
      <label className="text-xs text-muted-foreground">
        Amount off (¢)
        <input
          type="number"
          min={0}
          value={form.amount_off_cents ?? 0}
          onChange={(e) => set('amount_off_cents', Number(e.target.value))}
          className={inputClassName}
        />
      </label>
      <label className="text-xs text-muted-foreground">
        Max uses (blank = ∞)
        <input
          type="number"
          min={0}
          value={form.max_uses ?? ''}
          onChange={(e) => set('max_uses', e.target.value === '' ? null : Number(e.target.value))}
          className={inputClassName}
        />
      </label>
      <label className="text-xs text-muted-foreground">
        Expires (blank = never)
        <input
          type="date"
          value={secToDateInput(form.expires_at ?? null)}
          onChange={(e) => set('expires_at', dateInputToSec(e.target.value))}
          className={inputClassName}
        />
      </label>
      <label className="text-xs text-muted-foreground">
        Affiliate id
        <input
          value={form.affiliate_id ?? ''}
          onChange={(e) => set('affiliate_id', e.target.value)}
          placeholder="optional"
          className={inputClassName}
        />
      </label>
      <label className="flex items-end gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={!!form.first_month_only}
          onChange={(e) => set('first_month_only', e.target.checked)}
          className="h-4 w-4 accent-primary"
        />
        First month only
      </label>
      <label className="flex items-end gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={form.active !== false}
          onChange={(e) => set('active', e.target.checked)}
          className="h-4 w-4 accent-primary"
        />
        Active
      </label>
      <div className="col-span-2 md:col-span-4">
        {error && <p className="mb-2 text-sm text-destructive">{error}</p>}
        <Button type="submit" disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  )
}

function OffersTab() {
  const [offers, setOffers] = useState<Offer[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<Offer | null>(null)

  const reload = () => {
    void adminListOffers()
      .then(setOffers)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not load offers.')
      })
  }

  useEffect(reload, [])

  const remove = (id: number) => {
    void adminDeleteOffer(id)
      .then(reload)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Delete failed.')
      })
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="mb-4 font-semibold">New offer</h2>
        <OfferForm
          key="create"
          initial={emptyOffer}
          submitLabel="Create offer"
          onDone={(o) => setOffers((list) => (list ? [o, ...list] : [o]))}
        />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {!offers && !error && <p className="text-sm text-muted-foreground">Loading…</p>}

      {offers && (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={thClassName}>Code</th>
                <th className={thClassName}>% off</th>
                <th className={thClassName}>¢ off</th>
                <th className={thClassName}>Uses</th>
                <th className={thClassName}>Expires</th>
                <th className={thClassName}>Affiliate</th>
                <th className={thClassName}>Active</th>
                <th className={thClassName}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {offers.map((o) => (
                <tr key={o.id}>
                  <td className={tdClassName}>
                    <code>{o.code}</code>
                  </td>
                  <td className={tdClassName}>{o.percent_off}</td>
                  <td className={tdClassName}>{o.amount_off_cents}</td>
                  <td className={tdClassName}>
                    {o.uses}
                    {o.max_uses != null ? ` / ${o.max_uses}` : ''}
                  </td>
                  <td className={tdClassName}>{fmtDateTime(o.expires_at)}</td>
                  <td className={tdClassName}>{o.affiliate_id ?? '—'}</td>
                  <td className={tdClassName}>{o.active ? 'yes' : 'no'}</td>
                  <td className={tdClassName}>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEditing(o)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(o.id)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {offers.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No offers yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="mb-4 font-semibold">
            Edit <code>{editing.code}</code>
          </h2>
          <OfferForm
            key={editing.id}
            initial={{ ...editing, id: editing.id } as OfferInput & { id: number }}
            submitLabel="Save changes"
            onDone={(o) => {
              setOffers((list) => (list ? list.map((x) => (x.id === o.id ? o : x)) : [o]))
              setEditing(null)
            }}
          />
          <Button variant="ghost" className="mt-2" onClick={() => setEditing(null)}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  )
}

// --- comps ----------------------------------------------------------------

function CompsTab({ expiringOnly }: { expiringOnly: boolean }) {
  const [comps, setComps] = useState<Comp[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [months, setMonths] = useState(12)
  const [reason, setReason] = useState('')
  const [pending, setPending] = useState(false)

  const reload = () => {
    const task = expiringOnly ? adminExpiringComps() : adminListComps()
    void task
      .then(setComps)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not load comps.')
      })
  }

  useEffect(reload, [expiringOnly])

  const grant = (e: React.FormEvent) => {
    e.preventDefault()
    setPending(true)
    setError(null)
    void adminGrantComp({ user_email: email.trim(), reason: reason.trim(), months })
      .then((c) => {
        setComps((list) => (list && !expiringOnly ? [c, ...list] : list))
        setEmail('')
        setReason('')
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Grant failed.')
      })
      .finally(() => setPending(false))
  }

  const act = (id: number, fn: (id: number) => Promise<Comp>) => {
    void fn(id)
      .then((updated) => {
        setComps((list) => (list ? list.map((c) => (c.id === updated.id ? updated : c)) : list))
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Action failed.')
      })
  }

  const nowSec = Math.floor(Date.now() / 1000)

  return (
    <div className="space-y-6">
      {!expiringOnly && (
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="mb-4 font-semibold">Grant comp</h2>
          <form onSubmit={grant} className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <label className="text-xs text-muted-foreground md:col-span-2">
              User email
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                type="email"
                placeholder="user@example.com"
                className={inputClassName}
              />
            </label>
            <label className="text-xs text-muted-foreground">
              Months (1–60)
              <input
                type="number"
                min={1}
                max={60}
                value={months}
                onChange={(e) => setMonths(Number(e.target.value))}
                className={inputClassName}
              />
            </label>
            <label className="text-xs text-muted-foreground">
              Reason
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="optional"
                className={inputClassName}
              />
            </label>
            <div className="md:col-span-4">
              <Button type="submit" disabled={pending}>
                {pending ? 'Granting…' : 'Grant comp'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
      {!comps && !error && <p className="text-sm text-muted-foreground">Loading…</p>}

      {comps && (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={thClassName}>Email</th>
                <th className={thClassName}>Expires</th>
                <th className={thClassName}>Revoked</th>
                <th className={thClassName}>Renewals</th>
                <th className={thClassName}>Reason</th>
                {!expiringOnly && <th className={thClassName}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {comps.map((c) => {
                const secsLeft = (c.expires_at ?? 0) - nowSec
                const urgent = c.expires_at != null && secsLeft <= 7 * 24 * 3600
                return (
                  <tr
                    key={c.id}
                    className={urgent ? 'bg-amber-500/10' : undefined}
                    title={urgent ? 'Expiring within 7 days' : undefined}
                  >
                    <td className={tdClassName}>{c.email ?? c.user_id}</td>
                    <td className={tdClassName}>{fmtDateTime(c.expires_at)}</td>
                    <td className={tdClassName}>{c.revoked ? 'yes' : 'no'}</td>
                    <td className={tdClassName}>{c.renewed_count}</td>
                    <td className={tdClassName}>{c.reason || '—'}</td>
                    {!expiringOnly && (
                      <td className={tdClassName}>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => act(c.id, adminExtendComp)}>
                            Extend +12mo
                          </Button>
                          {!c.revoked && (
                            <Button size="sm" variant="ghost" onClick={() => act(c.id, adminRevokeComp)}>
                              Revoke
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
              {comps.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-sm text-muted-foreground">
                    {expiringOnly ? 'Nothing expiring in the next 7 days.' : 'No comps yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// --- paypal ----------------------------------------------------------------

function PaypalTab() {
  const [settings, setSettings] = useState<PaypalSettings | null>(null)
  const [clientId, setClientId] = useState('')
  const [secret, setSecret] = useState('')
  const [mode, setMode] = useState('sandbox')
  const [webhookId, setWebhookId] = useState('')
  const [pending, setPending] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void adminGetPaypalSettings()
      .then((s) => {
        setSettings(s)
        setClientId(s.client_id ?? '')
        setMode(s.mode)
        setWebhookId(s.webhook_id ?? '')
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not load PayPal settings.')
      })
  }, [])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setPending(true)
    setError(null)
    setSaved(false)
    void adminUpdatePaypalSettings({
      client_id: clientId,
      ...(secret ? { secret } : {}),
      mode,
      webhook_id: webhookId,
    })
      .then(() => {
        setSaved(true)
        setSecret('')
        return adminGetPaypalSettings()
      })
      .then(setSettings)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Save failed.')
      })
      .finally(() => setPending(false))
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <h2 className="mb-1 font-semibold">PayPal settings</h2>
      <p className="mb-4 text-sm text-muted-foreground">
        The secret is write-only and is never displayed.{' '}
        {settings && (
          <span>
            Secret stored: <strong>{settings.has_secret ? 'yes' : 'no'}</strong>
            {settings.plan_id ? ` · plan ${settings.plan_id}` : ' · no PayPal plan yet'}
            {settings.updated_at ? ` · updated ${fmtDateTime(settings.updated_at)}` : ''}
          </span>
        )}
      </p>
      {error && <p className="mb-3 text-sm text-destructive">{error}</p>}
      {saved && <p className="mb-3 text-sm text-green-500">Saved.</p>}
      <form onSubmit={submit} className="max-w-md space-y-3">
        <label className="block text-xs text-muted-foreground">
          Client ID
          <input
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="PayPal client id"
            className={inputClassName}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Secret (leave blank to keep the stored one)
          <input
            type="password"
            autoComplete="new-password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="••••••••"
            className={inputClassName}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Mode
          <select value={mode} onChange={(e) => setMode(e.target.value)} className={inputClassName}>
            <option value="sandbox">sandbox</option>
            <option value="live">live</option>
          </select>
        </label>
        <label className="block text-xs text-muted-foreground">
          Webhook ID (from the PayPal dashboard webhook details)
          <input
            value={webhookId}
            onChange={(e) => setWebhookId(e.target.value)}
            placeholder="e.g. 8RU…"
            className={inputClassName}
          />
        </label>
        <Button type="submit" disabled={pending}>
          {pending ? 'Saving…' : 'Save'}
        </Button>
      </form>
    </div>
  )
}

// --- users -----------------------------------------------------------------

function UsersTab() {
  const [users, setUsers] = useState<AdminUserRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tempPw, setTempPw] = useState<{ email: string; password: string } | null>(null)

  useEffect(() => {
    void adminListUsers()
      .then(setUsers)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not load users.')
      })
  }, [])

  const resetPw = (id: number, email: string) => {
    void adminTempPassword(id)
      .then((r) => setTempPw({ email, password: r.temp_password }))
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Reset failed.')
      })
  }

  const reopen = (id: number) => {
    void adminReopenUser(id)
      .then(() => adminListUsers())
      .then(setUsers)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Reopen failed.')
      })
  }

  return (
    <div>
      {error && <p className="mb-3 text-sm text-destructive">{error}</p>}
      {tempPw && (
        <p className="mb-3 rounded-md border border-border bg-card p-3 text-sm">
          Temporary password for <strong>{tempPw.email}</strong>:{' '}
          <code className="select-all font-mono">{tempPw.password}</code> — send it to
          them now, it will not be shown again.
        </p>
      )}
      {!users && !error && <p className="text-sm text-muted-foreground">Loading…</p>}
      {users && (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={thClassName}>ID</th>
                <th className={thClassName}>Email</th>
                <th className={thClassName}>Created</th>
                <th className={thClassName}>Sub</th>
                <th className={thClassName}>Grace</th>
                <th className={thClassName}>Comp</th>
                <th className={thClassName}>Comp expires</th>
                <th className={thClassName}>Closed</th>
                <th className={thClassName}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className={u.closed ? 'opacity-60' : undefined}>
                  <td className={tdClassName}>{u.id}</td>
                  <td className={tdClassName}>{u.email}</td>
                  <td className={tdClassName}>{fmtDateTime(u.created_at)}</td>
                  <td className={tdClassName}>{u.sub_status}</td>
                  <td className={tdClassName}>{u.in_grace ? 'yes' : 'no'}</td>
                  <td className={tdClassName}>{u.has_comp ? 'yes' : 'no'}</td>
                  <td className={tdClassName}>{fmtDateTime(u.comp_expires_at)}</td>
                  <td className={tdClassName}>{u.closed ? 'yes' : 'no'}</td>
                  <td className={tdClassName}>
                    <div className="flex gap-2">
                      {u.closed ? (
                        <Button size="sm" variant="outline" onClick={() => reopen(u.id)}>
                          Reopen
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => resetPw(u.id, u.email)}>
                          Reset pw
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No users yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// --- affiliates ---------------------------------------------------------------

function AffiliatesTab() {
  const [rows, setRows] = useState<AffiliateApplication[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<number | null>(null)

  const reload = useCallback(() => {
    void adminListAffiliates()
      .then(setRows)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not load applications.')
      })
  }, [])

  useEffect(reload, [reload])

  const setReviewed = (id: number, reviewed: boolean) => {
    void adminReviewAffiliate(id, reviewed)
      .then((updated) => {
        setRows((list) => list?.map((r) => (r.id === id ? updated : r)) ?? null)
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Update failed.')
      })
  }

  const remove = (id: number) => {
    void adminDeleteAffiliate(id)
      .then(() => {
        setRows((list) => list?.filter((r) => r.id !== id) ?? null)
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Delete failed.')
      })
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <h2 className="mb-1 font-semibold">Affiliate applications</h2>
      <p className="mb-4 text-sm text-muted-foreground">
        From the /affiliates form. Mark reviewed once contacted.
      </p>
      {error && <p className="mb-3 text-sm text-destructive">{error}</p>}
      {!rows && <p className="text-sm text-muted-foreground">Loading…</p>}
      {rows && rows.length === 0 && <p className="text-sm text-muted-foreground">No applications yet.</p>}
      {rows && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={thClassName}>Date</th>
                <th className={thClassName}>Name</th>
                <th className={thClassName}>Email</th>
                <th className={thClassName}>Type</th>
                <th className={thClassName}>Reviewed</th>
                <th className={thClassName}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <>
                  <tr key={r.id} className={r.reviewed ? 'opacity-60' : undefined}>
                    <td className={tdClassName}>{fmtDateTime(r.created_at)}</td>
                    <td className={tdClassName}>{r.name}</td>
                    <td className={tdClassName}>{r.email}</td>
                    <td className={tdClassName}>{r.kind}</td>
                    <td className={tdClassName}>{r.reviewed ? 'yes' : 'no'}</td>
                    <td className={tdClassName}>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setOpenId(openId === r.id ? null : r.id)}>
                          {openId === r.id ? 'Hide' : 'View'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setReviewed(r.id, !r.reviewed)}>
                          {r.reviewed ? 'Reopen' : 'Done'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => remove(r.id)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {openId === r.id && (
                    <tr key={`${r.id}-detail`}>
                      <td colSpan={6} className={tdClassName}>
                        <p className="mb-1 text-xs text-muted-foreground">About</p>
                        <p className="mb-3 whitespace-pre-wrap text-sm">{r.about}</p>
                        <p className="mb-1 text-xs text-muted-foreground">Heard via</p>
                        <p className="whitespace-pre-wrap text-sm">{r.heard_from}</p>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// --- page ------------------------------------------------------------------

const tabs: Array<{ id: Tab; label: string }> = [  { id: 'offers', label: 'Offers' },
  { id: 'comps', label: 'Comps' },
  { id: 'expiring', label: 'Expiring soon' },
  { id: 'paypal', label: 'PayPal' },
  { id: 'users', label: 'Users' },
  { id: 'affiliates', label: 'Affiliates' },
]

function AdminPage() {
  const { me, checking, denied } = useAdminGate()
  const [tab, setTab] = useState<Tab>('offers')

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AccountHeader />
      <div className="mx-auto max-w-6xl px-6 py-8">
        <h1 className="mb-1 text-2xl font-bold">Admin</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          {me ? `Signed in as ${me.email}` : 'Billing & access console'}
        </p>

        {checking && <p className="text-sm text-muted-foreground">Checking admin access…</p>}

        {!checking && denied && (
          <div className="rounded-xl border border-border bg-card p-8 text-center">
            <p className="mb-4 font-medium">Not authorized</p>
            <Button asChild variant="outline">
              <Link to="/">Back to home</Link>
            </Button>
          </div>
        )}

        {!checking && !denied && (
          <>
            <div className="mb-6 flex flex-wrap gap-2">
              {tabs.map((t) => (
                <Button
                  key={t.id}
                  variant={tab === t.id ? 'default' : 'outline'}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                </Button>
              ))}
            </div>
            {tab === 'offers' && <OffersTab />}
            {tab === 'comps' && <CompsTab expiringOnly={false} />}
            {tab === 'expiring' && <CompsTab key="expiring" expiringOnly />}
            {tab === 'paypal' && <PaypalTab />}
            {tab === 'users' && <UsersTab />}
            {tab === 'affiliates' && <AffiliatesTab />}
          </>
        )}
      </div>
    </div>
  )
}
